import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import type { ManagedUser, Workspace } from '../api/types';
import { useWorkspace } from '../workspace-context';
import { Stack, Typography } from './mui-system';

export function WorkspaceManagementDialog({ onClose }: { onClose(): void }) {
  const { activeWorkspace, refreshWorkspaces } = useWorkspace();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [selected, setSelected] = useState<Workspace | null>(null);
  const [name, setName] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const initialId = activeWorkspace?.id;

  function select(workspace: Workspace | null) {
    setSelected(workspace);
    setName(workspace?.name ?? '');
    setSiteUrl(workspace?.siteUrl ?? '');
    setOwnerId(workspace?.ownerId ?? '');
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.adminWorkspaces(), api.access()])
      .then(([rows, access]) => {
        if (cancelled) return;
        setWorkspaces(rows);
        setUsers(access.users);
        select(rows.find((row) => row.id === initialId) ?? rows[0] ?? null);
      })
      .catch((caught) => {
        if (!cancelled) setError(message(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialId]);

  async function mutate(action: () => Promise<unknown>, success: string) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await action();
      const rows = await api.adminWorkspaces();
      setWorkspaces(rows);
      select(rows.find((row) => row.id === selected?.id) ?? rows[0] ?? null);
      await refreshWorkspaces();
      setNotice(success);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSaving(false);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const changes = {
      ...(name.trim() !== selected.name ? { name: name.trim() } : {}),
      ...(siteUrl.trim() !== selected.siteUrl ? { siteUrl: siteUrl.trim() } : {}),
      ...(ownerId !== selected.ownerId ? { ownerId } : {}),
    };
    await mutate(() => api.updateWorkspace(selected.id, changes), 'Воркспейс сохранён');
  }

  const changed =
    selected &&
    (name.trim() !== selected.name ||
      siteUrl.trim() !== selected.siteUrl ||
      ownerId !== selected.ownerId);
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <Stack component="form" onSubmit={save}>
        <DialogTitle>Управление воркспейсами</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="success">{notice}</Alert>}
            {loading && <Typography role="status">Загрузка воркспейсов…</Typography>}
            {!loading && !error && workspaces.length === 0 && (
              <Alert severity="info">Воркспейсов пока нет.</Alert>
            )}
            {selected && (
              <>
                <TextField
                  select
                  label="Воркспейс для редактирования"
                  value={selected.id}
                  disabled={saving}
                  onChange={(event) => {
                    select(workspaces.find((row) => row.id === event.target.value) ?? null);
                    setError('');
                    setNotice('');
                  }}
                >
                  {workspaces.map((row) => (
                    <MenuItem key={row.id} value={row.id}>
                      {row.name} · {row.isShared ? 'общий' : 'личный'}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Название воркспейса"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={saving}
                  slotProps={{ htmlInput: { minLength: 2, maxLength: 80 } }}
                />
                <TextField
                  label="URL сайта"
                  value={siteUrl}
                  onChange={(event) => setSiteUrl(event.target.value)}
                  type="url"
                  required
                  disabled={saving}
                />
                <TextField
                  select
                  label="Владелец"
                  value={ownerId}
                  onChange={(event) => setOwnerId(event.target.value)}
                  disabled={saving}
                  helperText="Личный воркспейс доступен владельцу. Общий — всем пользователям с доступом."
                >
                  {!users.some((user) => user.id === selected.ownerId) && (
                    <MenuItem value={selected.ownerId} disabled>
                      Неизвестный владелец · {selected.ownerId}
                    </MenuItem>
                  )}
                  {users
                    .filter((user) => user.hasAccess || user.id === selected.ownerId)
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id} disabled={!user.hasAccess}>
                        {user.displayName} · {user.email}
                        {!user.hasAccess ? ' · доступ закрыт' : ''}
                      </MenuItem>
                    ))}
                </TextField>
                <Typography color="text.secondary" fontSize={12}>
                  Создан {new Date(selected.createdAt).toLocaleString('ru')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  {!selected.isShared && (
                    <Button
                      disabled={saving || Boolean(changed)}
                      onClick={() => {
                        const previous = workspaces.find((row) => row.isShared);
                        if (
                          window.confirm(
                            `Сделать «${selected.name}» общим для всех пользователей?${previous ? ` «${previous.name}» станет личным и останется доступен только владельцу.` : ''}`,
                          )
                        ) {
                          void mutate(
                            () => api.shareWorkspace(selected.id),
                            'Общий воркспейс изменён',
                          );
                        }
                      }}
                    >
                      Сделать общим
                    </Button>
                  )}
                  <Button
                    color="error"
                    disabled={saving || Boolean(selected.isShared) || Boolean(changed)}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Удалить «${selected.name}»? Сервер разрешит удаление только пустого воркспейса.`,
                        )
                      ) {
                        void mutate(
                          () => api.deleteWorkspace(selected.id),
                          'Пустой воркспейс удалён',
                        );
                      }
                    }}
                  >
                    Удалить пустой воркспейс
                  </Button>
                </Stack>
                <Typography color="text.secondary" fontSize={12}>
                  Для удаления общего воркспейса сначала назначьте другой общий.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Закрыть
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !changed || name.trim().length < 2 || !siteUrl.trim()}
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}

function message(error: unknown) {
  return error instanceof Error ? error.message : 'Не удалось изменить воркспейс';
}
